import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeads, Lead } from '../../crm-leads/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface RecentLeadsProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentLeads: React.FC<RecentLeadsProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { leads, isLoading } = useLeads();

  const items = React.useMemo(() => {
    const arr = Array.isArray(leads) ? leads : [];
    return [...arr]
      .sort((a: Lead, b: Lead) => {
        const ta = new Date(a.leadCreatedAt || 0).getTime();
        const tb = new Date(b.leadCreatedAt || 0).getTime();
        return tb - ta;
      })
      .slice(0, effectiveLimit);
  }, [leads, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  const formatCurrency = (v?: number) =>
    v != null ? `€${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';

  return (
    <GenericListWidget<Lead>
      title="Recent Leads"
      viewAllPath="/leads"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No leads"
      getItemKey={(l) => l.leadId}
      embedded={embedded}
      renderItem={(lead) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/leads/${lead.leadId}`)}
          onKeyDown={(e) => e.key === 'Enter' && navigate(`/leads/${lead.leadId}`)}
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text}`}>
              {lead.client?.clientName || `Lead #${lead.leadId}`}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {lead.leadStatusDescr?.as_descr && `${lead.leadStatusDescr.as_descr} · `}
              {lead.leadValue != null && formatCurrency(lead.leadValue)}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default RecentLeads;
